import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import { MdArrowBack } from "react-icons/md";
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiBookOpen, FiShield, FiArrowRight, FiCheck } from "react-icons/fi";

import { BuilderContext } from "./BuilderContext";
import ModalComponent from "../../../../components/Tenant/ModalComponent/ModalComponent";
import { listDrafts, saveDraft, deleteDraft } from "./ruleDrafts";
import {
  useGetMetaSchemaQuery, useGetWalletsQuery, useGetRuleByIdMutation,
  useCreateRuleMutation, useUpdateRuleMutation, useUpdateRuleStatusMutation,
  useGetRuleAppUserTypesQuery, useLazyGetSchemeFiltersQuery,
} from "../../../../features/tenant/rules/rulesApi";
import {
  emptyDraft, validateDraft, stepOfPath, serialize, fromRule, emptyAction, resolveSchemeId, resolveSchemeIds,
} from "./ruleModel";
import { plainEnglish } from "./summary";
import { States } from "../../../../utils/getStates";
import { Btn, StepBar, cx, Chip } from "./ui";

import StepTrigger from "./StepTrigger";
import StepConditions from "./StepConditions";
import StepRewardMode from "./StepRewardMode";
import StepActions from "./StepActions";
import StepSchedule from "./StepSchedule";

const STEPS = [
  { label: "Trigger", desc: "Rule event trigger & template" },
  { label: "Conditions", desc: "Fact checks & historical criteria" },
  { label: "Reward", desc: "Frequency, cooldown & limits" },
  { label: "Actions", desc: "Points, multipliers & slabs" },
  { label: "Schedule", desc: "Dates, priority & activation" },
];

export default function RuleWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const token = sessionStorage.getItem("access_token");
  const tenantId = JSON.parse(sessionStorage.getItem("vendorData") || "{}")?.tenant_id;
  const auth = { token, tenant_id: tenantId };

  const { data: meta, isLoading: metaLoading } = useGetMetaSchemaQuery(auth, { skip: !token });
  const { data: wallets } = useGetWalletsQuery(auth, { skip: !token });
  const { data: userTypesRaw } = useGetRuleAppUserTypesQuery(auth, { skip: !token });
  const [loadRule, ruleRes] = useGetRuleByIdMutation();
  const [createRule] = useCreateRuleMutation();
  const [updateRule] = useUpdateRuleMutation();
  const [updateStatus] = useUpdateRuleStatusMutation();
  const [fetchSchemeFilters] = useLazyGetSchemeFiltersQuery();

  const [draft, setDraft] = useState(emptyDraft());
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [schemeFilters, setSchemeFilters] = useState(null);

  // ---- frontend-only local draft state (matching scheme wizard) ----
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftsList, setDraftsList] = useState([]);

  useEffect(() => {
    setDraftsList(listDrafts());
  }, []);

  const appUserTypes = useMemo(
    () => (userTypesRaw || []).map((u) => ({ value: u.user_type, label: u.user_type })).filter((o) => o.value),
    [userTypesRaw]
  );
  const states = useMemo(() => States.map((s) => ({ value: s.name, label: s.name })), []);

  // Resolve the target scheme and load its eligibility (to scope user_type/state/category).
  const schemeId = useMemo(() => resolveSchemeId(draft.condition), [draft.condition]);
  const schemeIds = useMemo(() => resolveSchemeIds(draft.condition), [draft.condition]);
  const schemeScoped = draft.event_type === "scheme_scan" || draft.event_type === "points_scheme";
  useEffect(() => {
    if (schemeScoped && schemeId) {
      fetchSchemeFilters({ ...auth, scheme_id: schemeId }).unwrap().then(setSchemeFilters).catch(() => setSchemeFilters(null));
    } else {
      setSchemeFilters(null);
    }
  }, [schemeId, schemeScoped]); // eslint-disable-line

  useEffect(() => {
    if (isEdit) {
      loadRule({ ...auth, id }).unwrap().then((r) => {
        const rule = r?.body ?? r?.data ?? r;
        // Only draft / inactive rules are editable (backend enforces the same).
        if (rule?.status && rule.status !== "draft" && rule.status !== "inactive") {
          toast.error(`A ${rule.status === "archived" ? "deleted" : rule.status} rule can't be edited`);
          navigate(-1);
          return;
        }
        setDraft(fromRule(rule));
      }).catch(() => toast.error("Could not load rule"));
    }
  }, [id]); // eslint-disable-line

  // Detect cloneFrom state when navigated from RulesList
  const cloneFromRef = useRef(false);
  useEffect(() => {
    if (!isEdit && location.state?.cloneFrom && !cloneFromRef.current) {
      cloneFromRef.current = true;
      const rawRule = location.state.cloneFrom;
      const clonedDraft = fromRule(rawRule);
      setDraft({
        ...clonedDraft,
        name: rawRule.name ? `${rawRule.name} (Copy)` : "Cloned Rule Draft",
      });
      toast.success(`Loaded draft from rule: ${rawRule.name || "Rule"}`);
    }
  }, [isEdit, location.state]);

  const handleCloneRule = (rawRule) => {
    const clonedDraft = fromRule(rawRule);
    setDraft({
      ...clonedDraft,
      name: rawRule.name ? `${rawRule.name} (Copy)` : "Cloned Rule Draft",
    });
    setStep(0);
    toast.success(`Loaded draft from: ${rawRule.name || "Rule"}`);
  };

  const errors = useMemo(() => validateDraft(draft, meta, wallets, schemeFilters), [draft, meta, wallets, schemeFilters]);
  const stepHasError = (n) => errors.some((e) => stepOfPath(e.path) === n && e.severity === "error");
  const anyError = errors.some((e) => e.severity === "error");

  const update = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const applyTemplate = (t) => { setDraft(t.build()); setStep(0); toast.success(`Loaded: ${t.title}`); };
  const setEventType = (ev) =>
    setDraft((d) => ({
      ...d, event_type: ev,
      condition: d.event_type === ev ? d.condition : null,
      actions: (d.actions || []).map((a) => (a.type === "CREDIT_DYNAMIC" ? { ...emptyAction("CREDIT_DYNAMIC"), wallet_type: a.wallet_type } : a)),
    }));

  // ---- draft handlers ----
  const handleSaveDraftLocally = () => {
    if (isEdit) return;
    const dId = activeDraftId || Date.now();
    if (!activeDraftId) setActiveDraftId(dId);
    const saved = saveDraft({
      id: dId,
      name: draft.name || "Untitled Rule Draft",
      currentStep: step,
      data: draft,
    });
    setLastSavedAt(saved.updatedAt);
    setDraftsList(listDrafts());
    toast.success("Draft saved locally");
  };

  const handleApplyDraft = (d) => {
    if (d.data) {
      setDraft(d.data);
      setStep(d.currentStep || 0);
      setActiveDraftId(d.id);
      setShowDrafts(false);
      toast.success(`Resumed draft: ${d.name || "Untitled"}`);
    }
  };

  const handleDeleteDraftLocally = (dId) => {
    deleteDraft(dId);
    setDraftsList(listDrafts());
    if (dId === activeDraftId) setActiveDraftId(null);
    toast.success("Draft deleted");
  };

  const save = async (status) => {
    if (status === "active" && anyError) { toast.error("Fix all errors before activating"); return; }
    if (!draft.name?.trim()) { setStep(0); toast.error("Rule name is required"); return; }
    setSaving(true);
    try {
      const body = serialize(draft, isEdit ? undefined : status === "active" ? "active" : "draft");
      if (isEdit) {
        await updateRule({ ...auth, id, body }).unwrap();
        if (status === "active") await updateStatus({ ...auth, id, status: "active" }).unwrap();
        toast.success(status === "active" ? "Rule updated and activated" : "Rule updated");
      } else {
        await createRule({ ...auth, body }).unwrap();
        if (activeDraftId) deleteDraft(activeDraftId);
        toast.success(`Rule created as ${status}`);
      }
      navigate(-1);
    } catch (e) {
      toast.error(e?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (metaLoading || (isEdit && ruleRes.isLoading)) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-500">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <span className="text-sm font-medium">Loading capability matrix…</span>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="m-6 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-700 shadow-sm flex items-start gap-3">
        <FiAlertCircle className="h-5 w-5 text-rose-500 shrink-0 mt-0.5" />
        <div>
          <h4 className="font-semibold text-rose-800">Capability Matrix Unavailable</h4>
          <p className="mt-1">Could not load rule capabilities from <code className="bg-rose-100 px-1 rounded text-xs">/meta/schema</code>. Ensure the backend API service is operational.</p>
        </div>
      </div>
    );
  }

  const ctx = { token, tenantId, meta, wallets: wallets || [], eventType: draft.event_type, appUserTypes, states, schemeFilters, schemeId, schemeIds };
  const stepProps = { draft, errors, onChange: update };
  const savedLabel = lastSavedAt ? `Saved ${new Date(lastSavedAt).toLocaleTimeString()}` : "";

  return (
    <BuilderContext.Provider value={ctx}>
      <div className="min-h-screen bg-gray-50/60 pb-12 w-full">
        {/* Top Sticky Header — Full Width */}
        <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-2xs w-full">
          <div className="w-full px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors flex items-center gap-1.5 text-sm font-medium"
              >
                <MdArrowBack className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="h-4 w-px bg-gray-200 hidden sm:block" />
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg font-bold text-gray-900">{isEdit ? "Edit Rule" : "Rule Creation Wizard"}</h1>
                  <Chip color={anyError ? "rose" : "emerald"}>
                    {anyError ? "Draft has issues" : "Valid Configuration"}
                  </Chip>
                  {schemeIds.map((id) => {
                    const name = (wallets || []).find((w) => String(w.scheme_id) === String(id))?.name || `Scheme #${id}`;
                    return (
                      <span key={id} className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                        <span className="truncate max-w-[140px]">{name}</span>
                      </span>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 hidden sm:block">{STEPS[step]?.desc}</p>
              </div>
            </div>

            {/* Header Right Action Group with Draft Management */}
            <div className="flex items-center gap-3">
              {savedLabel && (
                <span className="hidden md:inline-flex items-center gap-1 text-xs text-emerald-600 font-medium bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                  <FiCheck className="h-3.5 w-3.5" /> {savedLabel}
                </span>
              )}

              {!isEdit && (
                <>
                  <Btn variant="ghost" onClick={handleSaveDraftLocally} className="text-xs sm:text-sm">
                    Save Draft
                  </Btn>
                  <button
                    onClick={() => { setDraftsList(listDrafts()); setShowDrafts(true); }}
                    className="px-3.5 py-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs sm:text-sm font-semibold transition-colors flex items-center gap-1.5 shadow-2xs"
                  >
                    <span>Drafts</span>
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white/20 text-[11px] font-bold">
                      {draftsList.length}
                    </span>
                  </button>
                </>
              )}

              {isEdit && (
                <Btn variant="ghost" disabled={saving || anyError} onClick={() => save("keep")} className="text-xs sm:text-sm">
                  Save Changes
                </Btn>
              )}
              <Btn disabled={saving || anyError} onClick={() => save("active")} className="text-xs sm:text-sm">
                {saving ? "Saving..." : "Activate Rule"}
              </Btn>
            </div>
          </div>
        </div>

        {/* Main Body Container — Full Width */}
        <div className="w-full px-6 lg:px-8 pt-6">
          {/* Stepper Card */}
          <div className="mb-6 rounded-2xl border border-gray-200/80 bg-white p-5 shadow-xs w-full">
            <StepBar steps={STEPS} current={step} onGo={setStep} hasError={(i) => stepHasError(i + 1)} />
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_360px] items-start w-full">
            {/* Active step workspace */}
            <div className="rounded-2xl border border-gray-200/80 bg-white p-6 md:p-8 shadow-xs min-h-[460px] flex flex-col justify-between w-full">
              <div className="flex-1 w-full">
                {step === 0 && <StepTrigger {...stepProps} onEventType={setEventType} onTemplate={!isEdit ? applyTemplate : null} onCloneRule={handleCloneRule} />}
                {step === 1 && <StepConditions draft={draft} errors={errors} onChange={(c) => update({ condition: c })} />}
                {step === 2 && <StepRewardMode {...stepProps} />}
                {step === 3 && <StepActions draft={draft} errors={errors} onChange={(a) => update({ actions: a })} />}
                {step === 4 && <StepSchedule {...stepProps} />}
              </div>

              <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between w-full">
                <Btn variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
                  Back
                </Btn>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 font-medium mr-2 hidden sm:inline">
                    Step {step + 1} of {STEPS.length}
                  </span>
                  {step < STEPS.length - 1 ? (
                    <Btn disabled={stepHasError(step + 1)} onClick={() => setStep((s) => s + 1)}>
                      Next Step <FiArrowRight className="ml-1" />
                    </Btn>
                  ) : (
                    <>
                      {isEdit && (
                        <Btn variant="ghost" disabled={saving || anyError} onClick={() => save("keep")}>
                          Save Changes
                        </Btn>
                      )}
                      <Btn disabled={saving || anyError} onClick={() => save("active")}>
                        Activate Rule
                      </Btn>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Live Side Panel */}
            <div className="flex flex-col gap-5 sticky top-20 w-full">
              {/* Plain English Summary */}
              <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-xs">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <FiBookOpen className="h-4 w-4 text-blue-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700">In Plain English</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50/70 rounded-xl p-3.5 border border-gray-100 font-medium">
                  {plainEnglish(draft, meta, wallets)}
                </p>
              </div>

              {/* Diagnostic Desk Validation Panel */}
              <ValidationPanel errors={errors} onGo={(n) => setStep(n - 1)} />
            </div>
          </div>
        </div>
      </div>

      {/* Saved Drafts List Modal */}
      <ModalComponent size="lg" show={showDrafts} setShow={setShowDrafts} showClose={true}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-3">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Saved Rule Drafts</h3>
              <p className="text-xs text-gray-500">Frontend localStorage drafts saved for this vendor tenant.</p>
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
              {draftsList.length} draft{draftsList.length === 1 ? "" : "s"}
            </span>
          </div>

          {draftsList.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm font-medium">No saved drafts found.</p>
              <p className="text-xs mt-1">Click "Save Draft" in the top bar while building a rule to save your progress locally.</p>
            </div>
          ) : (
            <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
              {draftsList.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between border border-gray-200/80 rounded-xl p-4 hover:border-gray-300 hover:bg-gray-50/50 transition-colors shadow-2xs"
                >
                  <div>
                    <div className="font-bold text-sm text-gray-900">{d.name || "Untitled Rule Draft"}</div>
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                      <span className="font-medium text-blue-700">Step {(d.currentStep || 0) + 1} of {STEPS.length}</span>
                      <span>•</span>
                      <span>{new Date(d.updatedAt).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApplyDraft(d)}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-2xs transition-colors"
                    >
                      Resume
                    </button>
                    <button
                      onClick={() => handleDeleteDraftLocally(d.id)}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-100 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalComponent>
    </BuilderContext.Provider>
  );
}

function ValidationPanel({ errors, onGo }) {
  const errs = errors.filter((e) => e.severity === "error");
  const warns = errors.filter((e) => e.severity === "warning");

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-xs">
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <FiShield className="h-4 w-4 text-blue-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-700">Diagnostic Desk</h3>
        </div>
        {errs.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700">
            {errs.length} issue{errs.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {errs.length === 0 && warns.length === 0 && (
        <div className="rounded-xl bg-emerald-50/80 border border-emerald-200/80 p-3.5 text-xs text-emerald-800 flex items-start gap-2.5 shadow-2xs">
          <FiCheckCircle className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold block text-emerald-900">All checks passed!</span>
            <span>Your rule configuration meets all validation constraints and is ready to activate.</span>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {errs.map((e, i) => {
          const stepNum = stepOfPath(e.path);
          return (
            <button
              key={`e${i}`}
              onClick={() => onGo(stepNum)}
              className="text-left rounded-xl bg-rose-50/70 border border-rose-200/80 p-3 text-xs text-rose-800 hover:bg-rose-100/70 transition-all group flex items-start gap-2.5 shadow-2xs"
            >
              <FiAlertCircle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-rose-900">Step {stepNum} Error</span>
                  <span className="text-[10px] font-semibold text-rose-700 underline group-hover:text-rose-900">Fix →</span>
                </div>
                <span>{e.message}</span>
              </div>
            </button>
          );
        })}

        {warns.map((e, i) => {
          const stepNum = stepOfPath(e.path);
          return (
            <button
              key={`w${i}`}
              onClick={() => onGo(stepNum)}
              className="text-left rounded-xl bg-amber-50/70 border border-amber-200/80 p-3 text-xs text-amber-800 hover:bg-amber-100/70 transition-all group flex items-start gap-2.5 shadow-2xs"
            >
              <FiAlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-amber-900">Step {stepNum} Warning</span>
                  <span className="text-[10px] font-semibold text-amber-700 underline group-hover:text-amber-900">Review →</span>
                </div>
                <span>{e.message}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


